import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { getData, addData, updateData, deleteData, getpastConfYears, getpastWebinarYears } from '../../services/pastEventSeoServices';
import { Modal, Button, Form } from 'react-bootstrap';
import DataTable from 'react-data-table-component';
import Header from '../../components/Layout/Header';
import Footer from '../../components/Layout/Footer';
import Sidebar from '../../components/Layout/Sidebar';
import Spinner from '../../components/Spinner';
import { toast } from 'react-toastify';
import Select from 'react-select';
import { validateFile } from '../../utils/validators';

const EVENT_TYPE_OPTIONS = [
  { value: 'conference', label: 'Past Conference' },
  { value: 'webinar', label: 'Past Webinar' },
];

const CONFERENCE_PAGE_TYPES = [
  { value: 'report', label: 'Report' },
  { value: 'proceedings', label: 'Proceedings' },
  { value: 'pc_pastaffilation', label: 'Attendees' },
  { value: 'gallery', label: 'Gallery' },
  { value: 'pc_testimonials', label: 'Testimonials' },
];

const WEBINAR_PAGE_TYPES = [
  { value: 'report', label: 'Report' },
  { value: 'proceedings', label: 'Proceedings' },
  { value: 'webinarvideos', label: 'Videos' },
  { value: 'pc_testimonials', label: 'Testimonials' },
];

const DEFAULTS = {
  id: '', event_type: '', year: '', page_type: '',
  meta_title: '', meta_description: '',
  og_type: 'website', og_locale: 'en_US', og_site_name: 'Scientex Conferences',
  twitter_card: 'summary', twitter_site: 'scientexevents',
  seo_image: null, uploadedfile: null,
};

const Pasteventseo = () => {
  const [dataList, setDataList] = useState([]);
  const [form, setForm] = useState(DEFAULTS);
  const [modalType, setModalType] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
  const [selectedData, setSelectedData] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [yearOptions, setYearOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);   // button-level loader, separate from page loading
  const [errors, setErrors] = useState({});

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getData();
      setDataList(data || []);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setTimeout(() => { setLoading(false); }, 500);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Year list depends on event_type — conference and webinar years live in
  // separate DB tables, so re-fetch whenever event_type changes.
  useEffect(() => {
    if (!form.event_type) {
      setYearOptions([]);
      return;
    }
    const fetchYears = async () => {
      try {
        const data = form.event_type === 'webinar'
          ? await getpastWebinarYears()
          : await getpastConfYears();
        setYearOptions((data || []).map((y) => ({ value: y.year, label: y.display_year })));
      } catch (error) {
        console.error('Error fetching years:', error);
        setYearOptions([]);
      }
    };
    fetchYears();
  }, [form.event_type]);

  const pageTypeOptions = form.event_type === 'webinar' ? WEBINAR_PAGE_TYPES : CONFERENCE_PAGE_TYPES;

  const validateForm = () => {
    const newErrors = {};
    if (!form.event_type) newErrors.event_type = "Event Type is required";
    if (!form.year) newErrors.year = "Year is required";
    if (!form.page_type) newErrors.page_type = "Page Type is required";
    if (!form.meta_title) newErrors.meta_title = "Meta Title is required";
    if (!form.meta_description) newErrors.meta_description = "Meta Description is required";
    if (form.meta_title.length < 20 || form.meta_title.length > 70)
      newErrors.meta_title = "Meta Title should be between 20 and 70 characters";
    if (form.meta_description.length < 70 || form.meta_description.length > 160)
      newErrors.meta_description = "Meta Description should be between 70 and 160 characters";
    if (!form.og_site_name) newErrors.og_site_name = "OG Site Name is required";
     if (!form.uploadedfile && !form.seo_image)
      newErrors.uploadedfile = "SEO Image is required";

    setErrors((prev) => ({ ...prev, ...newErrors }));
    return Object.keys({ ...errors, ...newErrors }).every((key) => !errors[key] && !newErrors[key]);
  };

  const handleChange = async (e) => {
    const { name, value, files } = e.target;
    const file = files?.[0];
    try {
      let updatedForm = { ...form };
      let updatedErrors = { ...errors };

      if (file) {
        const isValid = await validateFile(file, setErrors, name, 'img');
        if (!isValid) return;
        updatedForm[name] = file;
        updatedErrors[name] = '';
      } else {
        updatedForm[name] = value;
        if (value) updatedErrors[name] = '';

        // Changing event_type invalidates the currently selected year and
        // page_type, since both option lists differ between conference/webinar
        if (name === 'event_type') {
          updatedForm.year = '';
          updatedForm.page_type = '';
        }

        // Duplicate check on the (event_type, year, page_type) combination —
        // matches the DB's unique constraint, checked client-side too
        if (['event_type', 'year', 'page_type'].includes(name)) {
          const candidate = { ...updatedForm };
          const isDuplicate = dataList.some(
            (item) =>
              item.event_type === candidate.event_type &&
              item.year === candidate.year &&
              item.page_type === candidate.page_type &&
              item.id !== form.id
          );
          if (isDuplicate && candidate.event_type && candidate.year && candidate.page_type) {
            updatedErrors.page_type = "An entry already exists for this Event Type, Year, and Page Type";
          } else if (name === 'page_type') {
            updatedErrors.page_type = '';
          }
        }
      }
      setForm(updatedForm);
      setErrors(updatedErrors);
    } catch (error) {
      console.error('Error in handleChange:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    const fd = new FormData();
    fd.append('id', form.id);
    fd.append('event_type', form.event_type);
    fd.append('year', form.year);
    fd.append('page_type', form.page_type);
    fd.append('meta_title', form.meta_title);
    fd.append('meta_description', form.meta_description);
    fd.append('og_type', form.og_type);
    fd.append('og_locale', form.og_locale);
    fd.append('og_site_name', form.og_site_name);
    fd.append('twitter_card', form.twitter_card);
    fd.append('twitter_site', form.twitter_site);
    if (form.uploadedfile) fd.append('uploadedfile', form.uploadedfile);

    setSubmitting(true);
    try {
      const response = modalType === 'add' ? await addData(fd) : await updateData(fd);
      if (response.status === 200 || response.status === 201) {
        toast.success(modalType === 'add' ? "Added Successfully!" : "Updated Successfully!", { position: 'top-right', autoClose: 3000 });
        fetchData();
        setShowForm(false);
      } else {
        toast.error(response.message || "Failed to process request.", { position: 'top-right', autoClose: 3000 });
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error("Failed to process request.", { position: 'top-right', autoClose: 3000 });
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    try {
      if (selectedData) {
        setDataList((prev) => prev.filter((data) => data.id !== selectedData.id));
        await deleteData(selectedData.id);
        setShowDeleteConfirmModal(false);
        toast.success("Deleted Successfully!", { position: 'top-right', autoClose: 3000 });
      }
    } catch (error) {
      console.error('Error deleting data:', error);
    }
  };

  const openForm = (type, data = DEFAULTS) => {
    setModalType(type);
    setForm({ ...DEFAULTS, ...data });
    setShowForm(true);
    setErrors({});
  };

  const openDeleteConfirmModal = (data) => {
    setSelectedData(data);
    setShowDeleteConfirmModal(true);
  };

  const handleSearchChange = (e) => setSearchText(e.target.value);
  const handlePageChange = (page) => setCurrentPage(page);

  const filteredData = useMemo(() => {
    const filtered = dataList.filter(
      (data) =>
        data.year?.toLowerCase().includes(searchText.toLowerCase()) ||
        data.page_type?.toLowerCase().includes(searchText.toLowerCase()) ||
        data.meta_title?.toLowerCase().includes(searchText.toLowerCase())
    );
    return [...filtered].sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt));
  }, [dataList, searchText]);

  const columns = [
    { name: '#', selector: (row, index) => (currentPage - 1) * 10 + (index + 1), sortable: false, width: '5%' },
    { name: 'Event Type', selector: (row) => (<span title={row.event_type}>{row.event_type}</span>), sortable: true, width: '15%' },
    { name: 'Year', selector: (row) => (<span title={row.year}>{row.year}</span>), sortable: true, width: '12%' },
    { name: 'Page Type', selector: (row) => (<span title={row.page_type}>{row.page_type}</span>), sortable: true, width: '15%' },
    { name: 'Meta Title', selector: (row) => (<span title={row.meta_title}>{row.meta_title}</span>), sortable: true, width: '33%' },
    {
      name: 'Actions',
      cell: (row) => (
        <div>
          <Button className="me-2" variant="warning" size="sm" onClick={() => openForm('edit', row)}> Edit </Button>
          <Button variant="danger" size="sm" onClick={() => openDeleteConfirmModal(row)}> Delete </Button>
        </div>
      ),
      width: '20%'
    },
  ];

  const customStyles = (hasError) => ({
    control: (provided, state) => ({
      ...provided,
      borderColor: hasError ? '#f5c2c7' : provided.borderColor,
      boxShadow: hasError ? '0 0 0 1px rgba(167, 16, 31, 0.91)' : state.isFocused ? '0 0 0 1px #2684FF' : provided.boxShadow,
      '&:hover': { borderColor: hasError ? '#f5c2c7' : provided.borderColor },
    }),
  });

  return (
    <div className="d-flex">
      <Sidebar />
      <div className="main-content w-100">
        <Header />
        {loading ? (
          <div className="d-flex justify-content-center align-items-center w-100" style={{ minHeight: "300px" }}>
            <Spinner />
          </div>
        ) : (
          <div>
            <div className="container-fluid p-4">
              <div className="row">
                <div className="col-8"><p className='mb-3 fs-4'>SEO Config <b>/ Past Event SEO</b></p></div>
                {!showForm && (
                  <div className="col-4 text-end">
                    <button className="btn btn-primary mb-3 form-label-custom" onClick={() => openForm('add')}>Add</button>
                  </div>
                )}
              </div>

              {!showForm ? (
                <DataTable
                  columns={columns}
                  data={filteredData}
                  pagination search highlightOnHover responsive striped
                  paginationPerPage={10}
                  paginationComponentOptions={{ noRowsPerPage: true }}
                  onChangePage={handlePageChange}
                  subHeader
                  subHeaderComponent={
                    <input className="form-control" type="text" placeholder="Search" value={searchText} onChange={handleSearchChange} />
                  }
                />
              ) : (
                <div className="card p-4">
                  <h4 className='mb-4'>{modalType === 'add' ? 'Add' : 'Edit'} Past Event SEO</h4>
                  <Form>
                    <h6 className="text-muted mb-3">Section 1: Page Identity</h6>
                    <Form.Group className="mb-3">
                      <Form.Label className='form-label-custom'>Event Type<small className='text-danger'>*</small></Form.Label>
                      <Select
                        id="event_type"
                        name="event_type"
                        value={EVENT_TYPE_OPTIONS.find(opt => opt.value === form.event_type)}
                        onChange={(selectedOption) =>
                          handleChange({ target: { name: 'event_type', value: selectedOption?.value || '' } })
                        }
                        options={EVENT_TYPE_OPTIONS}
                        styles={customStyles(!!errors.event_type)}
                        placeholder="Select Event Type"
                      />
                      {errors.event_type && <div className="invalid-feedback d-block">{errors.event_type}</div>}
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label className='form-label-custom'>Year<small className='text-danger'>*</small></Form.Label>
                      <Select
                        id="year"
                        name="year"
                        value={yearOptions.find(opt => opt.value === form.year)}
                        onChange={(selectedOption) =>
                          handleChange({ target: { name: 'year', value: selectedOption?.value || '' } })
                        }
                        options={yearOptions}
                        styles={customStyles(!!errors.year)}
                        placeholder={form.event_type ? "Select Year" : "Select Event Type first"}
                        isDisabled={!form.event_type}
                      />
                      {errors.year && <div className="invalid-feedback d-block">{errors.year}</div>}
                    </Form.Group>

                    <Form.Group className="mb-3">
                      <Form.Label className='form-label-custom'>Page Type<small className='text-danger'>*</small></Form.Label>
                      <Select
                        id="page_type"
                        name="page_type"
                        value={pageTypeOptions.find(opt => opt.value === form.page_type)}
                        onChange={(selectedOption) =>
                          handleChange({ target: { name: 'page_type', value: selectedOption?.value || '' } })
                        }
                        options={pageTypeOptions}
                        styles={customStyles(!!errors.page_type)}
                        placeholder={form.event_type ? "Select Page Type" : "Select Event Type first"}
                        isDisabled={!form.event_type}
                      />
                      {errors.page_type && <div className="invalid-feedback d-block">{errors.page_type}</div>}
                    </Form.Group>

                    <hr />
                    <h6 className="text-muted mb-3">Section 2: Meta</h6>
                    <Form.Group className="mb-3">
                        <Form.Label className='form-label-custom'>Meta title <small className='text-danger'>*</small>
                        <small className='text-primary'> Char Limit: 20 -70 | Current Char Count: {form.meta_title.length}</small>
                        </Form.Label>
                        <Form.Control type="text" name="meta_title" value={form.meta_title} onChange={handleChange} isInvalid={!!errors.meta_title} />
                        <Form.Control.Feedback type="invalid">{errors.meta_title}</Form.Control.Feedback>
                    </Form.Group>
                    <Form.Group className="mb-3">
                    <Form.Label className='form-label-custom'>Meta description <small className='text-danger'>*</small>
                    <small className='text-primary'>Char Limit: 70 -160 | Current Char Count: {form.meta_description.length}</small>
                    </Form.Label>
                    <Form.Control as="textarea" rows={8} name="meta_description" value={form.meta_description} onChange={handleChange} isInvalid={!!errors.meta_description} />
                    <Form.Control.Feedback type="invalid">{errors.meta_description}</Form.Control.Feedback>
                </Form.Group>

                    <hr />
                    <h6 className="text-muted mb-3">Section 3: Open Graph Protocol</h6>
                    <Form.Group className="mb-3">
                      <Form.Label>OG Type</Form.Label>
                      <Form.Control type="text" value={form.og_type} readOnly disabled className="bg-light" />
                    </Form.Group>
                    <Form.Group className="mb-3">
                      <Form.Label>OG Locale</Form.Label>
                      <Form.Control type="text" value={form.og_locale} readOnly disabled className="bg-light" />
                    </Form.Group>
                    <Form.Group className="mb-3">
                      <Form.Label>OG Site Name <small className='text-danger'>*</small></Form.Label>
                      <Form.Control type="text" name="og_site_name" value={form.og_site_name} onChange={handleChange} isInvalid={!!errors.og_site_name} />
                      <Form.Control.Feedback type="invalid">{errors.og_site_name}</Form.Control.Feedback>
                    </Form.Group>
                    

                    <hr />
                    <h6 className="text-muted mb-3">Section 4: Twitter Card</h6>
                    <Form.Group className="mb-3">
                      <Form.Label>Twitter Card Type</Form.Label>
                      <Form.Control type="text" value={form.twitter_card} readOnly disabled className="bg-light" />
                    </Form.Group>
                    <Form.Group className="mb-3">
                      <Form.Label>Twitter Site</Form.Label>
                      <Form.Control type="text" value={form.twitter_site} readOnly disabled className="bg-light" />
                    </Form.Group>

                    <hr />
                    <h6 className="text-muted mb-3">SEO Image (used for og:image and twitter:image) (Image Dimension: 1200 x 630 | Kb: 10 - 150)</h6>
                    <Form.Group className="mb-3">
                      <Form.Control type="file" name="uploadedfile" accept="image/*" onChange={handleChange} isInvalid={!!errors.uploadedfile} />
                      <Form.Control.Feedback type="invalid">{errors.uploadedfile}</Form.Control.Feedback>
                      {form.seo_image && !form.uploadedfile && (
                        <img src={form.seo_image} alt="Current SEO" style={{ maxWidth: 200, marginTop: 8 }} />
                      )}
                      {form.uploadedfile && (
                        <img src={URL.createObjectURL(form.uploadedfile)} alt="New SEO preview" style={{ maxWidth: 200, marginTop: 8 }} />
                      )}
                    </Form.Group>

                    <div className="mt-4">
                      <Button variant="secondary" className="me-2" onClick={() => setShowForm(false)} disabled={submitting}>Cancel</Button>
                      <Button variant="primary" onClick={handleSubmit} disabled={submitting}>
                        {submitting ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                            Saving...
                          </>
                        ) : (modalType === 'add' ? 'Add' : 'Save')}
                      </Button>
                    </div>
                  </Form>
                </div>
              )}
            </div>

            <Modal show={showDeleteConfirmModal} onHide={() => setShowDeleteConfirmModal(false)}>
              <Modal.Header closeButton><Modal.Title>Delete</Modal.Title></Modal.Header>
              <Modal.Body><p>Are you sure you want to delete this row?</p></Modal.Body>
              <Modal.Footer>
                <Button variant="secondary" onClick={() => setShowDeleteConfirmModal(false)}>Cancel</Button>
                <Button variant="danger" onClick={handleDelete}>Yes, Delete</Button>
              </Modal.Footer>
            </Modal>

            <Footer />
          </div>
        )}
      </div>
    </div>
  );
};

export default Pasteventseo;
